import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { MetricCard } from "@/components/MetricCard";
import { ResponsiveMiniLineChart } from "@/components/ResponsiveMiniLineChart";
import { apiClient } from "@/lib/api";
import { UserReportsPage } from "@/pages/UserReportsPage";

export const UserPortfolioPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [portfolio, setPortfolio] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const activeTab = searchParams.get("tab") || "overview";

  const loadData = async ({ force = false } = {}) => {
    if (force) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    // P14-B fix: Promise.allSettled — biri çökse bile diğeri render olsun
    const portfolioUrl = force ? "/user/portfolio?force_refresh=true" : "/user/portfolio";
    const [portfolioRes, performanceRes] = await Promise.allSettled([
      apiClient.get(portfolioUrl),
      apiClient.get("/user/performance"),
    ]);
    if (portfolioRes.status === "fulfilled") {
      setPortfolio(portfolioRes.value.data);
    } else if (!force) {
      setPortfolio(null);
    }
    if (performanceRes.status === "fulfilled") {
      setPerformance(performanceRes.value.data);
    } else if (!force) {
      setPerformance(null);
    }
    setLastRefreshAt(new Date());
    setIsLoading(false);
    setIsRefreshing(false);
  };

  useEffect(() => {
    loadData({ force: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chartData = useMemo(
    () => [
      { metric: "Open", value: portfolio?.open_notional ?? 0 },
      { metric: "Avail", value: portfolio?.available_balance ?? 0 },
      { metric: "ClosedPnl", value: portfolio?.closed_pnl ?? 0 },
      { metric: "ROI", value: performance?.roi_pct ?? 0 },
    ],
    [performance, portfolio],
  );

  const hideMockWallet = false;

  if (isLoading) {
    return <LoadingSkeleton rows={5} testId="user-portfolio-loading-skeleton" />;
  }

  return (
    <section className="grid grid-cols-12 gap-4" data-testid="user-portfolio-page">
      <header className="col-span-12 border border-slate-800 bg-slate-900 p-4" data-testid="user-portfolio-header">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-4xl font-black uppercase tracking-tight" data-testid="user-portfolio-title">Portfolio</h2>
            <p className="mt-2 text-sm text-slate-400" data-testid="user-portfolio-description">
              12 kolon responsive düzen: mobilde taşmadan, desktop’ta yoğun veri görünümü.
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <button
              type="button"
              onClick={() => loadData({ force: true })}
              disabled={isRefreshing}
              className="rounded border border-emerald-500 bg-emerald-500/10 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-emerald-200 hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="user-portfolio-refresh-button"
              title="Cüzdan snapshot'larını borsadan zorla yenile (force_refresh)"
            >
              {isRefreshing ? "🔄 Yenileniyor…" : "🔄 Yenile"}
            </button>
            {lastRefreshAt ? (
              <span className="text-[10px] text-slate-500" data-testid="user-portfolio-last-refresh-at">
                Son: {lastRefreshAt.toLocaleTimeString()}
              </span>
            ) : null}
          </div>
        </div>
      </header>

      <div className="col-span-12 grid grid-cols-12 gap-3" data-testid="user-portfolio-summary-grid">
        <div className="col-span-12 grid gap-2" data-testid="user-portfolio-wallet-stack">
          <div data-testid="user-portfolio-total-wallet-balance-card">
            <MetricCard label="Toplam Cüzdan Bakiyesi" value={portfolio?.total_wallet_balance ?? 0} tone="orange" testId="user-portfolio-total-wallet-balance" />
          </div>
          <div data-testid="user-portfolio-spot-wallet-balance-card">
            <MetricCard label="Spot Bakiyesi" value={portfolio?.spot_wallet_balance ?? 0} tone="blue" testId="user-portfolio-spot-wallet-balance" />
          </div>
          {portfolio?.wallet_status && portfolio.wallet_status !== "ok" ? (
            <div className="rounded border border-amber-700 bg-amber-950/30 px-3 py-2 text-xs text-amber-200" data-testid="user-portfolio-wallet-status-banner">
              <p className="font-medium" data-testid="user-portfolio-wallet-status">
                {(portfolio.selected_exchange_market_type || "Spot").toString().charAt(0).toUpperCase() + (portfolio.selected_exchange_market_type || "spot").toString().slice(1)} wallet status: {portfolio.wallet_status}
                {portfolio.wallet_status_reason ? ` (${portfolio.wallet_status_reason})` : ""}
              </p>
              {portfolio.selected_exchange_connection_name ? (
                <p className="mt-1 text-slate-300" data-testid="user-portfolio-selected-exchange">
                  Connection: <span className="font-mono">{portfolio.selected_exchange_connection_name}</span>
                  {" · "}
                  <span>{portfolio.selected_exchange_market_type}</span>
                  {" · "}
                  <span>{portfolio.selected_exchange_environment}</span>
                </p>
              ) : null}
              {portfolio.selected_exchange_health_message ? (
                <p className="mt-1 text-amber-100" data-testid="user-portfolio-wallet-health-message">
                  {portfolio.selected_exchange_health_message}
                </p>
              ) : portfolio.wallet_status === "non_stable_only" ? (
                <p className="mt-1 text-amber-100" data-testid="user-portfolio-wallet-non-stable-note">
                  Stable bakiyeniz 0 ama wallet'ta {portfolio.non_zero_assets_count} adet stable olmayan asset var. Bunları Binance'da USDT'ye çevirin.
                </p>
              ) : null}
            </div>
          ) : null}
          <div data-testid="user-portfolio-futures-wallet-balance-card">
            <MetricCard label="Futures Bakiyesi" value={portfolio?.futures_wallet_balance ?? 0} tone="orange" testId="user-portfolio-futures-wallet-balance" />
          </div>
        </div>

        {hideMockWallet && (
          <p className="col-span-12 text-xs text-amber-300" data-testid="user-portfolio-mock-wallet-hidden-note">
            Pure Live aktif.
          </p>
        )}
        <div className="col-span-12 flex flex-wrap gap-2" data-testid="user-portfolio-tab-group">
          {[
            ["overview", "Overview"],
            ["pnl", "PnL"],
            ["reports", "Reports"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setSearchParams({ tab: value })}
              className={`rounded border px-3 py-2 text-sm ${activeTab === value ? "border-emerald-400 bg-emerald-400/20 text-emerald-200" : "border-slate-700 bg-slate-900 text-slate-300"}`}
              data-testid={`user-portfolio-tab-${value}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="col-span-6 md:col-span-4 xl:col-span-2"><MetricCard label="Capital" value={hideMockWallet ? "-" : (portfolio?.current_capital ?? "-")} tone="orange" testId="user-portfolio-current-capital" /></div>
        <div className="col-span-6 md:col-span-4 xl:col-span-2"><MetricCard label="Balance" value={hideMockWallet ? "-" : (portfolio?.available_balance ?? "-")} tone="blue" testId="user-portfolio-available-balance" /></div>
        <div className="col-span-6 md:col-span-4 xl:col-span-2"><MetricCard label="Open Notional" value={hideMockWallet ? "-" : (portfolio?.open_notional ?? "-")} tone="orange" testId="user-portfolio-open-notional" /></div>
        <div className="col-span-6 md:col-span-4 xl:col-span-2"><MetricCard label="Open Pos" value={hideMockWallet ? "-" : (portfolio?.open_positions_count ?? "-")} tone="blue" testId="user-portfolio-open-count" /></div>
        <div className="col-span-6 md:col-span-4 xl:col-span-2"><MetricCard label="Closed PnL" value={hideMockWallet ? "-" : (portfolio?.closed_pnl ?? "-")} tone="orange" testId="user-portfolio-closed-pnl" /></div>
        <div className="col-span-6 md:col-span-4 xl:col-span-2"><MetricCard label="Compounding" value={hideMockWallet ? "-" : (portfolio?.compounding_enabled ? "ON" : "OFF")} tone="blue" testId="user-portfolio-compounding" /></div>
      </div>

      {activeTab !== "reports" && <div className="col-span-12 lg:col-span-8" data-testid="user-portfolio-chart-col">
        <ResponsiveMiniLineChart data={chartData} xKey="metric" yKey="value" title="Portfolio Curve" testId="user-portfolio-responsive-chart" />
      </div>}

      {activeTab !== "reports" && <div className="col-span-12 lg:col-span-4 grid grid-cols-12 gap-3" data-testid="user-portfolio-performance-grid">
        <div className="col-span-6 lg:col-span-12"><MetricCard label="Win Rate" value={performance?.win_rate ?? "-"} tone="blue" testId="user-performance-win-rate" /></div>
        <div className="col-span-6 lg:col-span-12"><MetricCard label="ROI %" value={performance?.roi_pct ?? "-"} tone="orange" testId="user-performance-roi" /></div>
        <div className="col-span-6 lg:col-span-12"><MetricCard label="Profit Factor" value={performance?.profit_factor ?? "-"} tone="blue" testId="user-performance-profit-factor" /></div>
        <div className="col-span-6 lg:col-span-12"><MetricCard label="Exec Quality" value={performance?.avg_execution_quality ?? "-"} tone="orange" testId="user-performance-execution-quality" /></div>
      </div>}

      {activeTab === "reports" && (
        <div className="col-span-12" data-testid="user-portfolio-reports-embedded-panel">
          <UserReportsPage embedded />
        </div>
      )}
    </section>
  );
};